import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { SNIPPET_TYPES, extOf, withExt } from '@stepflow/ui-shared/utils/codeLang.js';

/**
 * CODE SNIPPET (CONTEXT-DOCS.md §P4) — the composer's `</>` (Slack's "Create a text snippet"): paste code, name it,
 * pick its type → it becomes a real FILE attached to the message (``onAdd(File)`` → the normal attach pipeline), so
 * it is kept exactly as written, read into the context like any shared file, and shows in the stream as a code panel.
 * The type and the file name stay in step: picking a type sets the extension; typing a known extension picks the type.
 */
export function snippetFile(name, code, type = 'txt') {
  const fileName = SNIPPET_TYPES.some((t) => t.ext === extOf(name)) ? String(name).trim() : withExt(name, type);
  return new File([String(code || '')], fileName, { type: 'text/plain' });
}

export default function CodeSnippetDialog({ onAdd, onCancel }) {
  const [name, setName] = useState('snippet.txt');
  const [type, setType] = useState('txt');
  const [code, setCode] = useState('');
  const codeRef = useRef(null);
  useEffect(() => { if (codeRef.current) codeRef.current.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const pickType = (ext) => { setType(ext); setName((n) => withExt(n, ext)); };
  const editName = (n) => {
    setName(n);
    const e = extOf(n);
    if (SNIPPET_TYPES.some((t) => t.ext === e)) setType(e);
  };
  // Tab indents (a code box) instead of leaving the field
  const onCodeKey = (e) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    e.preventDefault();
    const el = e.currentTarget;
    const a = el.selectionStart; const b = el.selectionEnd;
    const next = `${code.slice(0, a)}  ${code.slice(b)}`;
    setCode(next);
    requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = a + 2; });
  };
  const lines = code ? code.replace(/\n$/, '').split('\n').length : 0;
  const ok = code.trim().length > 0 && name.trim().length > 0;
  const add = () => { if (ok) onAdd(snippetFile(name, code, type)); };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true"
      aria-label="Create a code snippet" data-testid="code-snippet-dialog">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-gray-200 px-5 py-3">
          <div className="flex-1 text-[0.9375rem] font-bold text-gray-900">Create a code snippet</div>
          <button type="button" onClick={onCancel} aria-label="Close" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100">
            <X className="h-[1.125rem] w-[1.125rem]" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4 text-[0.8125rem]">
          <div className="flex gap-3">
            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-[0.75rem] font-medium text-gray-600">File name</span>
              <input data-testid="code-snippet-name" value={name} onChange={(e) => editName(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 font-mono text-[0.8125rem] focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300" />
            </label>
            <label className="flex w-48 flex-col gap-1">
              <span className="text-[0.75rem] font-medium text-gray-600">Type</span>
              <select data-testid="code-snippet-type" value={type} onChange={(e) => pickType(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[0.8125rem] focus:border-indigo-400 focus:outline-none">
                {SNIPPET_TYPES.map((t) => <option key={t.ext} value={t.ext}>{t.label}</option>)}
              </select>
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-[0.75rem] font-medium text-gray-600">Content</span>
            <textarea ref={codeRef} data-testid="code-snippet-code" value={code} onChange={(e) => setCode(e.target.value)}
              onKeyDown={onCodeKey} spellCheck={false} rows={14} placeholder="Paste or type your code"
              className="resize-y rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[0.8125rem] leading-[1.5] focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300" />
          </label>
        </div>
        <div className="flex items-center gap-2 border-t border-gray-200 px-5 py-3">
          <span className="flex-1 text-[0.75rem] text-gray-500" data-testid="code-snippet-lines">
            {lines ? `${lines} line${lines === 1 ? '' : 's'}` : 'Shared as a file — kept exactly as written'}
          </span>
          <button type="button" onClick={onCancel} className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-100">Cancel</button>
          <button type="button" onClick={add} disabled={!ok} data-testid="code-snippet-add"
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            Add to message
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
